import { BlockBaseConfig, ContactDetailsBlockType } from "./types";
import { z } from "zod";
import { i18n } from "@tellescope/i18n";
import tagsPreview from "./assets/block-previews/tags-block-preview.svg";
import notesPreview from "./assets/block-previews/notes-block-preview.svg";
import fieldsPreview from "./assets/block-previews/fields-block-preview.svg";
import careTeamPreview from "./assets/block-previews/care-team-block-preview.svg";
import journeysPreview from "./assets/block-previews/journeys-block-preview.svg";
import relatedContactsPreview from "./assets/block-previews/related-contacts-block-preview.svg";
import aiSummaryPreview from "./assets/block-previews/ai-summary-block-preview.svg";
import timelinePreview from "./assets/block-previews/timeline-block-preview.svg";
import ticketsPreview from "./assets/block-previews/tickets-block-preview.svg";
import calendarPreview from "./assets/block-previews/calendar-block-preview.svg";
import eligibilityPreview from "./assets/block-previews/eligibility-block-preview.svg";
import ordersPreview from "./assets/block-previews/orders-block-preview.svg";
import filesPreview from "./assets/block-previews/files-block-preview.svg";
import formsPreview from "./assets/block-previews/forms-block-preview.svg";
import contentPreview from "./assets/block-previews/content-block-preview.svg";
import medicationsPreview from "./assets/block-previews/medications-block-preview.svg";
import paymentsPreview from "./assets/block-previews/payments-block-preview.svg";
import vitalsPreview from "./assets/block-previews/vitals-block-preview.svg";

export const baseBlockInfoSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  expanded: z.boolean().optional(),
});

const freeformFilterSchema = z.object({
  id: z.string(),
  logic: z.enum(["and", "or"]),
  filters: z.array(
    z.object({
      field: z.string(),
      operation: z.enum([
        "equals",
        "does_not_equal",
        "is_set",
        "is_not_set",
        "contains",
        "does_not_contain",
        "before",
        "after",
        "less_than",
        "greater_than",
      ]),
      value: z.union([z.string(), z.number(), z.array(z.string())]).optional(),
    })
  ),
});

/**
 * The six models the timeline hook reads to build its merged item stream. Shared by the timeline and
 * AI-summary blocks, which both surface that same stream.
 */
const TIMELINE_READ_MODELS = [
  "phone_calls",
  "form_responses",
  "sms_messages",
  "chat_rooms",
  "emails",
  "notes",
] as const;

export const CONTACT_DETAILS_BLOCKS_FLAT_MAP = {
  ["information.tags"]: {
    previewUrl: tagsPreview,
    getName: () => i18n.t("Tags", { ns: "contacts" }),
    getDescription: () => i18n.t("Organize contacts with labels and categories", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      filter: freeformFilterSchema.optional(),
    }),
  },
  ["information.notes"]: {
    previewUrl: notesPreview,
    getName: () => i18n.t("Notes", { ns: "contacts" }),
    getDescription: () => i18n.t("One time use freeform notes", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
  },
  ["information.fields"]: {
    previewUrl: fieldsPreview,
    getName: () => i18n.t("Fields", { ns: "contacts" }),
    getDescription: () => i18n.t("Display and edit custom contact fields", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      filter: freeformFilterSchema.optional(),
    }),
  },
  ["information.care-team"]: {
    previewUrl: careTeamPreview,
    getName: () => i18n.t("Care Team", { ns: "contacts" }),
    getDescription: () => i18n.t("Care team members assigned to this contact", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["users"],
  },
  ["information.journeys"]: {
    previewUrl: journeysPreview,
    getName: () => i18n.t("Journeys", { ns: "contacts" }),
    getDescription: () => i18n.t("See, add, and remove contacts from journeys", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      filter: freeformFilterSchema.optional(),
    }),
    requiredReadModels: ["journeys"],
  },
  ["related-contacts.related-contacts"]: {
    previewUrl: relatedContactsPreview,
    getName: () => i18n.t("Related Contacts", { ns: "contacts" }),
    getDescription: () => i18n.t("View and manage related patient contacts and their roles", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      filter: freeformFilterSchema.optional(),
    }),
    requiredReadModels: ["endusers"],
  },
  ["timeline.ai-summary"]: {
    previewUrl: aiSummaryPreview,
    getName: () => i18n.t("AI Summary", { ns: "contacts" }),
    getDescription: () => i18n.t("AI-generated summary of patient interactions", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      focus: z.string().optional(),
    }),
    requiredReadModels: TIMELINE_READ_MODELS,
  },
  ["timeline.timeline"]: {
    previewUrl: timelinePreview,
    getName: () => i18n.t("Timeline", { ns: "contacts" }),
    getDescription: () => i18n.t("Patient interactions in chronological order", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      filter: freeformFilterSchema.optional(),
      combineSMS: z.boolean().optional(),
    }),
    requiredReadModels: TIMELINE_READ_MODELS,
  },
  ["tickets.tickets"]: {
    previewUrl: ticketsPreview,
    getName: () => i18n.t("Tickets", { ns: "contacts" }),
    getDescription: () => i18n.t("Open and resolved support tickets for this contact", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      filter: freeformFilterSchema.optional(),
    }),
    requiredReadModels: ["tickets", "users"],
    requiredFeature: "tickets",
  },
  ["calendar.calendar"]: {
    previewUrl: calendarPreview,
    getName: () => i18n.t("Calendar", { ns: "contacts" }),
    getDescription: () => i18n.t("Upcoming and past appointments for this contact", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema.extend({
      filter: freeformFilterSchema.optional(),
    }),
    requiredReadModels: ["calendar_events"],
    requiredFeature: "calendar",
  },
  ["eligibility.eligibility"]: {
    previewUrl: eligibilityPreview,
    getName: () => i18n.t("Eligibility", { ns: "contacts" }),
    getDescription: () => i18n.t("Insurance eligibility and benefit verification results", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["enduser_eligibility_results"],
    requiredFeature: "eligibility",
  },
  ["orders.orders"]: {
    previewUrl: ordersPreview,
    getName: () => i18n.t("Orders", { ns: "contacts" }),
    getDescription: () => i18n.t("View patient orders and fulfillment status", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["enduser_orders"],
    requiredFeature: "orders",
  },
  ["files.files"]: {
    previewUrl: filesPreview,
    getName: () => i18n.t("Files", { ns: "contacts" }),
    getDescription: () => i18n.t("Documents and attachments uploaded for this contact", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["files"],
    requiredFeature: "files",
  },
  ["forms.forms"]: {
    previewUrl: formsPreview,
    getName: () => i18n.t("Forms", { ns: "contacts" }),
    getDescription: () => i18n.t("Form submission history and responses for this contact", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["form_responses", "forms"],
    requiredFeature: "forms",
  },
  ["content.content"]: {
    previewUrl: contentPreview,
    getName: () => i18n.t("Content", { ns: "contacts" }),
    getDescription: () => i18n.t("Managed content assigned to this contact", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["managed_content_records", "managed_content_record_assignments"],
    requiredFeature: "content",
  },
  ["medications.medications"]: {
    previewUrl: medicationsPreview,
    getName: () => i18n.t("Medications", { ns: "contacts" }),
    getDescription: () => i18n.t("Active and past medications with dosage and prescriber details", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["enduser_medications"],
    requiredFeature: "medications",
  },
  ["payments.payments"]: {
    previewUrl: paymentsPreview,
    getName: () => i18n.t("Payments", { ns: "contacts" }),
    getDescription: () => i18n.t("Purchase history and payment actions for this contact", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["purchases", "products"],
    requiredFeature: "payments",
  },
  ["vitals.vitals"]: {
    previewUrl: vitalsPreview,
    getName: () => i18n.t("Vitals", { ns: "contacts" }),
    getDescription: () => i18n.t("Vital sign readings, trends, and threshold alerts", { ns: "contacts" }),
    infoSchema: baseBlockInfoSchema,
    requiredReadModels: ["enduser_observations"],
    requiredFeature: "vitals",
  },
} as const satisfies Record<string, BlockBaseConfig & { infoSchema?: z.AnyZodObject }>;

export const getBlockConfig = <K extends ContactDetailsBlockType>(
  type: K
): (typeof CONTACT_DETAILS_BLOCKS_FLAT_MAP)[K] => CONTACT_DETAILS_BLOCKS_FLAT_MAP[type];
